import React, { useContext, useState } from "react";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import { isModernChatLayout, normalizeChatLayoutMode } from "../../helpers/chatLayoutMode";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Troca layout do chat (legado / Tailwick). Uso: barra superior da lista, no lugar do nome do app.
 */
export default function ChatLayoutToggle({
	compact = true,
	useTailwickButton = false,
	useTailwickIcon = false,
	inline = false,
	asMenuItem = false,
	onAfterClick,
}) {
	const { user, updateChatLayoutMode } = useContext(AuthContext);
	const [saving, setSaving] = useState(false);

	const mode = normalizeChatLayoutMode(user?.chatLayoutMode);
	const isModern = isModernChatLayout(mode);

	const applyMode = async (next) => {
		const nextMode = normalizeChatLayoutMode(next);
		if (nextMode === mode || saving) return;
		setSaving(true);
		if (updateChatLayoutMode) {
			updateChatLayoutMode({ ...user, chatLayoutMode: nextMode });
		}
		try {
			const { data } = await api.patch("/users/me/chat-layout", {
				chatLayoutMode: nextMode,
			});
			if (data?.user && updateChatLayoutMode) {
				updateChatLayoutMode(data.user);
			}
			toast.success(i18n.t("chat.layout.switched"));
		} catch (err) {
			if (updateChatLayoutMode) {
				updateChatLayoutMode({ ...user, chatLayoutMode: mode });
			}
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const label = isModern
		? i18n.t("chat.layout.backToClassic")
		: i18n.t("chat.layout.tryNewExperience");

	const handleToggle = async () => {
		await applyMode(isModern ? "legacy" : "modern");
		if (onAfterClick) onAfterClick();
	};

	if (asMenuItem) {
		return (
			<button
				type="button"
				role="menuitem"
				className="tailwick-chats-header-menu__item"
				disabled={saving}
				onClick={handleToggle}
			>
				{saving ? "..." : label}
			</button>
		);
	}

	if (useTailwickIcon) {
		const railLabel = isModern
			? i18n.t("chat.layout.classicLayoutRailLabel")
			: i18n.t("chat.layout.newExperienceBadge");

		if (inline) {
			return (
				<button
					type="button"
					className="tailwick-layout-rail-toggle tailwick-layout-rail-toggle--inline"
					disabled={saving}
					onClick={() => applyMode(isModern ? "legacy" : "modern")}
					title={i18n.t("chat.layout.toggleHint")}
					aria-label={isModern ? i18n.t("chat.layout.backToClassic") : label}
				>
					{saving ? (
						<span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
					) : (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden
						>
							<path d="m12 19-7-7 7-7" />
							<path d="M19 12H5" />
							<rect width="7" height="18" x="14" y="3" rx="1" opacity="0.45" />
						</svg>
					)}
				</button>
			);
		}

		return (
			<button
				type="button"
				className="tailwick-layout-rail-toggle flex w-full max-w-[4.5rem] flex-col items-center gap-1 rounded-md border-0 bg-transparent px-1 py-1.5 transition-colors hover:bg-primary/10 disabled:opacity-60"
				disabled={saving}
				onClick={() => applyMode(isModern ? "legacy" : "modern")}
				title={i18n.t("chat.layout.toggleHint")}
				aria-label={isModern ? i18n.t("chat.layout.backToClassic") : label}
			>
				<span className="flex size-9 items-center justify-center rounded-md text-default-600 hover:text-primary">
					{saving ? (
						<span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
					) : (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden
						>
							<path d="m12 19-7-7 7-7" />
							<path d="M19 12H5" />
							<rect width="7" height="18" x="14" y="3" rx="1" opacity="0.45" />
						</svg>
					)}
				</span>
				<span className="text-center text-[10px] font-medium leading-tight text-default-600">
					{railLabel}
				</span>
			</button>
		);
	}

	if (useTailwickButton) {
		return (
			<button
				type="button"
				className="card-title m-0 inline-flex w-full min-w-0 max-w-full items-center gap-1.5 border-0 bg-transparent p-0 text-left text-sm font-semibold text-default-800 transition-colors hover:text-primary disabled:opacity-60"
				disabled={saving}
				onClick={() => applyMode(isModern ? "legacy" : "modern")}
				title={i18n.t("chat.layout.toggleHint")}
			>
				{saving ? (
					<span className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent" />
				) : (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						className="shrink-0"
						aria-hidden
					>
						<path d="m15 18-6-6 6-6" />
					</svg>
				)}
				<span className="truncate">{label}</span>
			</button>
		);
	}

	return (
		<Tooltip title={i18n.t("chat.layout.toggleHint")}>
			<span style={{ display: "inline-block", maxWidth: "100%" }}>
				<Button
					variant={isModern ? "outlined" : "contained"}
					color="primary"
					size={compact ? "small" : "medium"}
					disabled={saving}
					onClick={() => applyMode(isModern ? "legacy" : "modern")}
					style={{
						textTransform: "none",
						fontWeight: 600,
						whiteSpace: "nowrap",
						maxWidth: "100%",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{saving ? (
						<CircularProgress size={18} color="inherit" style={{ marginRight: 6 }} />
					) : null}
					{label}
				</Button>
			</span>
		</Tooltip>
	);
}
